import * as React from "react"
import { usePage } from "@inertiajs/react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { MapPin } from "lucide-react"
import { RoomMeta } from "@/components/RoomMeta"
import { LocationMap } from "@/components/LocationMap"
import { HeroFilmSlides } from "@/components/gisela/HeroBand"
import { BrandMotif } from "@/components/ui/brand-motif"
import { cn } from "@/lib/utils"
import { headingSizeClass } from "@/lib/heading-size"
import type { PageProps } from "@/types/inertia"

/**
 * The bands a location is shown with — the venue's page at /<slug> and
 * Gisela's page for the same building at /therapy-in-<slug> are composed
 * from these. Each band takes the facts (rooms, photos, address) and the
 * words around them; which words, and which links, is the page's business,
 * so the venue can offer a viewing where her page offers a free call.
 */

export interface Photo {
  id: number
  url: string | null
  alt_text: string | null
}

export interface SiteRoom {
  id: number
  name: string
  description: string | null
  capacity: number | null
  size_label: string | null
  features: string[]
  photo_url: string | null
  /** The first photos of the room's own gallery section. */
  photos: Photo[]
  /** How many that section holds in all. */
  photo_count: number
}

export interface TourVideo {
  title: string
  embed_src: string | null
  file_url: string | null
  /** The still shown before it plays, chosen in the video library. */
  poster_url?: string | null
}

export interface HeroSlides {
  slides: Photo[]
  interval_seconds: number
  film?: string | null
  heading_size?: string | null
}

/** Replace `%{name}`-style placeholders in a locale string. */
export function fill(template: string, values: Record<string, string>): string {
  return template.replace(/%\{(\w+)\}/g, (_, key) => values[key] ?? "")
}

/** The photo slideshow hero both pages open on: eyebrow, headline, a line
 *  of copy, the address, then whatever calls to action the page passes. */
export function LocationHero({
  hero,
  eyebrow,
  heading,
  subheading,
  address,
  children,
}: {
  hero: HeroSlides
  eyebrow: string
  heading: string
  subheading?: string
  address: string
  children?: React.ReactNode
}) {
  const slides = hero.slides.filter((s): s is Photo & { url: string } => !!s.url)
  return (
    <section className="relative overflow-hidden bg-teal pt-32 pb-16 text-white">
      {/* One photo or several, depending on the section's display settings
          — a single slide simply doesn't rotate — under the same blue film
          as every other hero on both sites. */}
      <HeroFilmSlides slides={slides} intervalMs={hero.interval_seconds * 1000} film={hero.film} />
      <BrandMotif variant="rings" tone="white" size={720} opacity={0.35} className="absolute -top-80 -left-64" />
      <div className="relative mx-auto max-w-4xl px-6 text-center lg:px-8">
        <p className="mb-3 text-xs font-medium uppercase tracking-widest text-white/60">{eyebrow}</p>
        {/* Plain concatenation, not cn(): tailwind-merge reads .text-hero-xl
            as a text- utility and drops text-white as a conflict, which
            paints the heading accent blue on a blue band. */}
        <h1 className={`text-white ${headingSizeClass(hero.heading_size)}`.trim()}>{heading}</h1>
        {subheading && (
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/75">{subheading}</p>
        )}
        <div className="mt-4 flex items-center justify-center gap-1.5 text-white/60">
          <MapPin className="h-4 w-4" />
          <span className="text-sm">{address}</span>
        </div>
        {children}
      </div>
    </section>
  )
}

/** One room: its copy on one side, a mosaic of its own gallery photos on the
 *  other. `flip` puts the photos first (from lg up), so the rooms alternate.
 *  `gallery` is the room's gallery page — a path on the venue's site, an
 *  absolute URL from hers. `extra` is the page's own link beside "All photos". */
export function RoomBand({
  room,
  gallery,
  flip,
  first,
  extra,
}: {
  room: SiteRoom
  gallery: string
  flip: boolean
  first: boolean
  extra?: React.ReactNode
}) {
  const { translations } = usePage<PageProps>().props
  const t = translations.sanctuary_pages.location
  const tc = translations.sanctuary_pages.common
  const roomGallery = `${gallery}#room_${room.id}`
  // The room's gallery section, or — until it has one — the card photo the
  // Rooms list gives it.
  const photos = room.photos.filter((p): p is Photo & { url: string } => !!p.url)
  const pictures: { id: number; url: string; alt_text: string }[] =
    photos.length > 0
      ? photos.map((p) => ({ id: p.id, url: p.url, alt_text: p.alt_text ?? room.name }))
      : room.photo_url
        ? [{ id: 0, url: room.photo_url, alt_text: room.name }]
        : []
  const [lead, second] = pictures
  const count = Math.max(room.photo_count, pictures.length)

  return (
    <div className={cn("mx-auto max-w-7xl px-6 lg:px-8", first ? "mt-10 pb-16" : "border-t border-hairline py-16")}>
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center lg:gap-12">
        <div className={cn(flip && "lg:order-last")}>
          <h3>{room.name}</h3>
          {room.description && <p className="mt-3">{room.description}</p>}
          <RoomMeta capacity={room.capacity} size_label={room.size_label} features={room.features} />
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2">
            {count > 0 && (
              <a href={roomGallery} className="text-sm font-medium text-teal hover:underline">
                {fill(t.all_photos, { name: room.name })}
              </a>
            )}
            {extra}
          </div>
        </div>

        {lead ? (
          // A large photo with a smaller one and the way into the rest beside
          // it; on a phone the small pair sits under the large one.
          <div className="grid grid-cols-2 grid-rows-[220px_110px] gap-3 lg:grid-cols-3 lg:grid-rows-[150px_150px]">
            <a href={roomGallery} className="col-span-2 block overflow-hidden rounded-2xl lg:row-span-2">
              <img src={lead.url} alt={lead.alt_text} className="h-full w-full object-cover" />
            </a>
            {second && (
              <a href={roomGallery} className="block overflow-hidden rounded-xl">
                <img src={second.url} alt={second.alt_text} className="h-full w-full object-cover" loading="lazy" decoding="async" />
              </a>
            )}
            <a
              href={roomGallery}
              className={cn(
                "flex items-center justify-center rounded-xl bg-stone-mid px-3 text-center text-sm font-medium text-teal ring-1 ring-hairline no-underline transition-shadow hover:shadow-md",
                !second && "col-span-2 lg:col-span-1",
              )}
            >
              {count > 1 ? fill(t.see_all_photos, { count: String(count) }) : tc.view_room_photos}
            </a>
          </div>
        ) : (
          <div className="flex h-56 items-center justify-center rounded-2xl bg-stone-mid ring-1 ring-hairline lg:h-[312px]">
            <span className="text-sm text-ink-muted">{tc.room_photo_coming_soon}</span>
          </div>
        )}
      </div>
    </div>
  )
}

/** The location's video tour: an embed or an uploaded file. */
export function VideoTourBand({ video, heading, className }: { video: TourVideo; heading: string; className?: string }) {
  return (
    <section className={cn(className, "py-16")}>
      <div className="mx-auto max-w-5xl px-6 lg:px-8">
        <h2 className="text-center">{heading}</h2>
        <div className="mt-8 aspect-video overflow-hidden rounded-2xl bg-stone-mid ring-1 ring-hairline">
          {video.embed_src ? (
            <iframe
              src={video.embed_src}
              title={video.title}
              className="h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          ) : (
            <video
              // With no poster chosen, #t=0.1 asks the browser to seek a
              // fraction in so it paints a frame rather than a black box.
              // With one, no fragment: the seek would replace the chosen
              // still with whatever the video happens to open on.
              src={video.poster_url ? video.file_url! : `${video.file_url!}#t=0.1`}
              poster={video.poster_url ?? undefined}
              controls
              preload="metadata"
              // object-cover so a poster that isn't 16:9 fills the band
              // rather than sitting in bars.
              className="h-full w-full object-cover"
            />
          )}
        </div>
      </div>
    </section>
  )
}

/** The building outside the rooms: copy beside a strip of the gallery's
 *  outside, reception and kitchen photos, each a way into the gallery. */
export function BuildingBand({
  heading,
  body,
  photos,
  gallery,
  galleryLabel,
  className,
}: {
  heading: string
  body?: string
  photos: Photo[]
  gallery: string
  galleryLabel: string
  className?: string
}) {
  const pictures = photos.filter((p): p is Photo & { url: string } => !!p.url)
  return (
    <section className={cn(className, "py-16")}>
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:items-center lg:gap-12">
          <div>
            <h2>{heading}</h2>
            {body && (
              <div className="mt-3 [&_p]:mt-2 [&_p:first-child]:mt-0">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
              </div>
            )}
            <a href={gallery} className="mt-6 inline-block text-sm font-medium text-teal hover:underline">
              {galleryLabel}
            </a>
          </div>
          {pictures.length > 0 && (
            <div className="grid grid-cols-3 gap-3">
              {pictures.map((photo) => (
                <a key={photo.id} href={gallery} className="block overflow-hidden rounded-xl">
                  <img
                    src={photo.url}
                    alt={photo.alt_text ?? ""}
                    className="h-24 w-full object-cover sm:h-36"
                    loading="lazy"
                    decoding="async"
                  />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export interface SitePlace {
  name: string
  address_lines: string[]
  maps_url: string | null
  latitude: number | null
  longitude: number | null
  opening_hours: string | null
}

/** Address, hours, map link and directions, beside a photo of the frontage
 *  or — failing one — the map. `lead` is the page's own words above the
 *  facts, if it has any. */
export function GettingHereBand({
  site,
  heading,
  lead,
  directions,
  photo,
  className,
}: {
  site: SitePlace
  heading: string
  lead?: string
  directions?: string
  photo?: Photo | null
  className?: string
}) {
  const { translations } = usePage<PageProps>().props
  const t = translations.sanctuary_pages.location
  const ts = translations.locations.show
  return (
    <section className={cn(className, "py-16")}>
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <h2>{heading}</h2>
        {lead && (
          <div className="mt-3 max-w-3xl text-ink-muted [&_p]:mt-2 [&_p:first-child]:mt-0">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{lead}</ReactMarkdown>
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div className="space-y-6">
            <div>
              <h3>{t.address}</h3>
              <p className="mt-2 text-ink-muted">
                {site.address_lines.map((line) => (
                  <React.Fragment key={line}>{line}<br /></React.Fragment>
                ))}
              </p>
              {site.maps_url && (
                <a
                  href={site.maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-block text-sm text-teal-mid hover:underline"
                >
                  {ts.view_on_google_maps}
                </a>
              )}
              {site.opening_hours && (
                <p className="mt-3 text-sm text-ink-muted">
                  {ts.opening_hours}: {site.opening_hours}
                </p>
              )}
            </div>

            {directions && (
              <div className="[&_h3]:mt-4 [&_h3:first-child]:mt-0 [&_p]:mt-2">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{directions}</ReactMarkdown>
              </div>
            )}
          </div>

          {/* Map / location photo */}
          {photo?.url ? (
            <img
              src={photo.url}
              alt={photo.alt_text ?? ""}
              className="h-72 w-full rounded-2xl object-cover ring-1 ring-hairline lg:h-auto"
            />
          ) : site.latitude != null && site.longitude != null ? (
            /* No photo placed: the map, which at least answers "where is
               this" rather than promising one later. A photo of the frontage
               still wins when there is one — arriving somewhere, the door
               you are looking for beats a pin. */
            <LocationMap
              latitude={site.latitude}
              longitude={site.longitude}
              name={site.name}
              mapsUrl={site.maps_url}
              mapLabel={t.map_of}
              directionsLabel={t.directions}
              className="lg:h-full"
            />
          ) : (
            <div className="flex h-72 items-center justify-center rounded-2xl bg-stone-mid ring-1 ring-hairline lg:h-auto">
              <div className="text-center">
                <MapPin className="mx-auto h-8 w-8 text-ink-muted" />
                <p className="mt-2 text-sm text-ink-muted">{t.map_coming_soon}</p>
                {site.maps_url && (
                  <a
                    href={site.maps_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-block text-sm text-teal-mid hover:underline"
                  >
                    {ts.view_on_google_maps}
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
